import { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { useSession } from '../../../techstyle-shared/redux-core';

import { getMembershipDetails } from './accountsModule';
import useAccountActions from './useAccountActions';

/**
 * Get membership detail values. Will fetch from API if needed.
 * @param {String[]} inputKeys An array of membership detail names to fetch.
 * @returns {String[]} An array of membership detail values.
 */
export default function useMembershipDetails(inputKeys) {
  if (typeof inputKeys === 'string') {
    inputKeys = [inputKeys];
  }
  const actions = useAccountActions();
  const { isLoggedIn } = useSession();

  // `inputKeys` will likely be passed as an inline array, so memoize it in
  // order to get a stable value and do less work.
  const keyString = JSON.stringify(inputKeys);
  const keys = useMemo(() => JSON.parse(keyString), [keyString]);

  const membershipDetails = useSelector((state) =>
    getMembershipDetails(state, keys)
  );

  const values = useMemo(
    () => keys.map((key) => membershipDetails[key]?.value),
    [membershipDetails, keys]
  );

  const isUpToDate = useMemo(
    () =>
      keys.every((key) => membershipDetails[key]?.networkStatus?.isUpToDate),
    [membershipDetails, keys]
  );

  useEffect(() => {
    if (isLoggedIn && !isUpToDate) {
      actions.loadMembershipDetails(keys);
    }
  }, [actions, isLoggedIn, isUpToDate, keys]);

  return values;
}
